import {AxiosResponse} from "axios";
import {keyCloakAPI, newBackendAPI} from "../../apiConsumer";
import {KEYCLOAK_REALM} from "global/constants";

type LoginDataType = {
  email: string;
  password: string;
};

type LoginReturn<T> = {
  data: {
    menu: Array<T>;
    token: string;
    user: {
      avatar: string;
      id: string;
      email: string;
      name: string;
      logged_at: string;
    };
  };
};

export type KeycloakLoginReturn<T> = {
  access_token: string;
  expires_in: number;
  refresh_expires_in: number;
  token_type: string;
  session_state: string;
  scope: string;
};

export type KeycloakUserInfoReturn = {
  sub: string;
  email?: string;
  email_verified?: boolean;
  name?: string;
  preferred_username?: string;
  given_name?: string;
  family_name?: string;
  sid?: string;
};

export const Authenticate = async <T>(
  loginData: LoginDataType,
): Promise<AxiosResponse<LoginReturn<T>>> => {
  return await newBackendAPI.post("/authenticate", loginData);
};

export const RequestPasswordReset = async (mail: string) => {
  return await newBackendAPI.post("/forgot-password", {email: mail});
};

export const ValidatePasswordResetCode = async (passwordResetCode: {
  email: string;
  code: string;
}) => {
  return await newBackendAPI.post("/validate-code", passwordResetCode);
};

export const UpdatePassword = async (newPassword: {
  email: string;
  password: string;
}) => {
  return await newBackendAPI.post("/update-password", newPassword);
};

export const KeyCloakAuthenticate = async <T>(
  loginData: LoginDataType,
): Promise<AxiosResponse<KeycloakLoginReturn<T>>> => {
  return await keyCloakAPI.post(
    `/realms/${KEYCLOAK_REALM}/protocol/openid-connect/token`,
    FormatKeyCloakAuthInfo(loginData),
  );
};

export const KeyCloakUserInfo = async (
  accessToken: string,
): Promise<AxiosResponse<KeycloakUserInfoReturn>> => {
  return await keyCloakAPI.get(
    `/realms/${KEYCLOAK_REALM}/protocol/openid-connect/userinfo`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );
};

const FormatKeyCloakAuthInfo = (loginData: LoginDataType) => {
  const {innerWidth: screenWidth} = window;

  const params = new URLSearchParams();

  params.append("grant_type", "password");
  params.append(
    "client_id",
    screenWidth > 768
      ? (import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? "")
      : (import.meta.env.VITE_KEYCLOAK_MOBILE_ID ?? ""),
  );
  params.append("scope", "openid");
  params.append("username", loginData.email);
  params.append("password", loginData.password);

  return params.toString();
};

export const KeyCloakRefreshTokenAuth = async <T>(
  refreshToken: string,
): Promise<AxiosResponse<KeycloakLoginReturn<T>>> => {
  return await keyCloakAPI.post(
    `/realms/${KEYCLOAK_REALM}/protocol/openid-connect/token`,
    FormatKeyCloakRefreshTokenReqInfo(refreshToken),
  );
};

const FormatKeyCloakRefreshTokenReqInfo = (refreshToken: string) => {
  const {innerWidth: screenWidth} = window;
  const params = new URLSearchParams();

  params.append("grant_type", "refresh_token");
  params.append(
    "client_id",
    screenWidth > 768
      ? (import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? "")
      : (import.meta.env.VITE_KEYCLOAK_MOBILE_ID ?? ""),
  );
  params.append("refresh_token", refreshToken);

  return params;
};
